/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * 一键绑卡-通用组件
 */
import { Component, Prop, Vue } from 'vue-property-decorator';
import { bindQuickBankList, cardQuickBindApply, cardQuickBindQuery } from '@/api/quick-bind-card';
import { BindCardResultEnum, IBankData } from '@/api/quick-bind-card/types';
import { ubtSendError } from '@/shared/utils/ubt';
import URLUtils from '@/shared/utils/URLUtils';
import { Toast } from '@hb/coffee';
import { session } from '@/shared/utils/storage';
import type { BindCardFlowTypeEnum } from '@/api/bank-card/types';
import {
  closeWebView, nativeLocalGetData, nativeLocalRemoveData, nativeLocalSetData, openThirdPage,
} from '@/api/native/utils';
import { CAN_USE_NEW_WEBVIEW_FOR_JUMP, NS_QUICK_BOUND_ORDER_DATA } from '@/shared/utils/quickBindCard';
import { listerBack } from '@/shared/utils/backListener';
import { BoundOrderData, QuickBindCardStatusEnum } from './types';
import BankCard from './components/bank-card';
import { AgreeProtocolDialog } from './components/agree-protocol';

import style from './index.module.scss';

const BIND_CARD_SUCCESS_SIGN = 'quick_bind_card_success_sign';
const BIND_CARD_BACK_LINK = 'bind_card_back_link';

@Component
export default class QuickBindCard extends Vue {
  @Prop(String) readonly title!: string; // 标题

  @Prop(String) readonly productId!: string; // 产品ID

  @Prop(String) readonly bindCardScene?: BindCardFlowTypeEnum; // 绑卡流程类型

  @Prop(String) readonly capitalCode!: string; // 资金编码

  @Prop(Boolean) readonly useNewWebviewForJump!: boolean; // 是否使用新开webview的方式进入通联页面

  private agreeProtocolDialogVisible = false; // 是否显示同意协议对话框

  /** 当前状态 */
  private componentStatus: QuickBindCardStatusEnum = QuickBindCardStatusEnum.LOADING; // 当前状态

  private cardListData: IBankData[] = []; // 银行卡数据

  /** 当前选中的银行 */
  private selectedBankData: IBankData | null = null; // 当前选中的银行数据

  /** 轮询查询绑卡结果最大次数 */
  private canQueryQuickBindCount = 3; // 查询绑卡结果最大次数

  /** 是否原始webview的绑卡页面(当使用新开webview进入通联页面时，此值为true) */
  private isOriginCardBindPageWebview = false;

  /** nativeStorage保存的通联绑卡回执订单数据 */
  private boundOrderDataForNS = {} as BoundOrderData;

  private clearBackOriginCardBindLister: (() => void) | null = null;

  /**
   * 通联绑卡的回执订单数据
   * 哈啰App-新开webview的方式，取客户端缓存
   * h5浏览器-location.href的方式，取页面url参数
   */
  private get boundOrderData(): BoundOrderData {
    if (this.isCommunityBoundLocationJump) {
      const query = URLUtils.getQueryMap() as Dictionary<string>;

      return {
        accountNo: query.ACCOUNT_NO,
        bindRequestNo: query.REQ_SN,
        cardApplyId: query.cardApplyId,
      };
    }

    return this.boundOrderDataForNS;
  }

  /**
   * 是否通联绑卡location.href的回跳场景
   * 通过URI链接参数：REQ_SN、ACCOUNT_NO、cardApplyId关键字段有值作为判断依据
   */
  private get isCommunityBoundLocationJump() {
    const query = URLUtils.getQueryMap() as Dictionary<string>;

    return query.REQ_SN && query.cardApplyId && query.ACCOUNT_NO;
  }

  /**
   * 是否通联绑卡location.href的回跳场景
   * 通过URI链接参数：REQ_SN、CARD_APPLY_ID关键字段有值作为判断依据
   * ACCOUNT_NO 关键字段没有值是因为用户绑卡时签约行查无此账号，此时会回跳到我们页面但是url上ACCOUNT_NO值为空
   * 此处的判断是为了解决https://hellobike.yuque.com/gpxxgb/oyyz8n/rrymscf62r13i1ch#ReAsJ中的问题3
   */
  private get boundLocationJumpWithNoAccountNo() {
    const query = URLUtils.getQueryMap() as Dictionary<string>;
    return query.REQ_SN && query.cardApplyId;
  }

  /** 是否使用新开Webview链路 */
  private get isUseNewWebviewToJumpCommunity() {
    return CAN_USE_NEW_WEBVIEW_FOR_JUMP && this.useNewWebviewForJump;
  }

  /** 一键绑卡回跳链接 */
  private get quickBindCardBackLink(): string {
    const curLink = `GET:${window.location.href}`;

    if (this.boundLocationJumpWithNoAccountNo) {
      return session.get(BIND_CARD_BACK_LINK) || curLink;
    }

    return curLink;
  }

  /**
   * 初始化银行卡列表数据
   */
  private async initCardListData() {
    const { bankList } = await bindQuickBankList({
      productId: this.productId,
      capitalCode: this.capitalCode,
    });
    this.cardListData = bankList || [];
    this.componentStatus = QuickBindCardStatusEnum.SELECTING;
  }

  /** 查询快捷绑卡最终的结果 */
  private queryQuickBindFinalResult() {
    const loopQueryBoundResult = async () => {
      const hasFinished = await this.queryQuickBindResult();

      if (hasFinished) {
        /** 重置是否原始webview状态 */
        this.isOriginCardBindPageWebview = false;
        return;
      }
      /** 轮询：次数，时间需要优化成可配置 */
      if (this.canQueryQuickBindCount-- > 0) {
        setTimeout(() => {
          loopQueryBoundResult();
        }, 1500);
      } else {
        Toast('绑卡失败，可尝试输卡号添加');
        this.$emit('failureQuickBindCard', {
          bankNo: this.boundOrderData.accountNo,
        });
        /** 重置是否原始webview状态 */
        this.isOriginCardBindPageWebview = false;
      }
    };
    this.showLoadingResult();
    /** 轮询查询绑卡结果 */
    loopQueryBoundResult();
  }

  async created(): Promise<void> {
    /** 新开Webview链路，通联绑卡成功回跳后，则设置绑卡订单回传数据，关闭当前webview */
    if (this.boundLocationJumpWithNoAccountNo && this.isUseNewWebviewToJumpCommunity) {
      // 只有是通联绑卡location.href的回跳场景，且三个关键字段都有值才需要设置绑卡订单回传数据
      if (this.isCommunityBoundLocationJump) {
        try {
          const { accountNo, bindRequestNo, cardApplyId } = this.boundOrderData;
          /** 用于记录通联绑卡回跳的链接 */
          const locationUrl = window.location.href;

          await nativeLocalSetData({
            key: NS_QUICK_BOUND_ORDER_DATA,
            value: [accountNo, bindRequestNo, cardApplyId, locationUrl],
          });
        } catch (err) {
          ubtSendError(err as Error);
          this.$ubtModuleExposeNew('fiance_quick_bind_card_error', {
            stage: 'setCacheData',
            ...this.boundOrderData,
          });
        }
      }

      closeWebView();
      return;
    }

    /** 非新开Webview的链路，通联绑卡成功回跳后，绑卡结果查询 */
    if (this.isCommunityBoundLocationJump) {
      this.$emit('afterBackBindCardPage');
      /** 通联绑卡成功回跳后，防止重复进入时，产生两次绑卡成功回调 */
      if (session.get(BIND_CARD_SUCCESS_SIGN) !== this.boundOrderData.bindRequestNo) {
        this.queryQuickBindFinalResult();
      }
    }
  }

  private async mounted(): Promise<void> {
    /** 新开Webview链路，通联绑卡回跳后，不展示页面绑卡相关内容 */
    if (this.boundLocationJumpWithNoAccountNo && this.isUseNewWebviewToJumpCommunity) {
      return;
    }

    await this.initCardListData();

    if (this.cardListData && this.cardListData.length) {
      this.$ubtModuleExposeNew('finance_cardBind_quickBinding_enter');
    }

    const { clearBackLister } = listerBack(async () => {
      if (this.isOriginCardBindPageWebview) {
        try {
          const dataArr = await nativeLocalGetData({ key: NS_QUICK_BOUND_ORDER_DATA }) as string[];

          if (dataArr?.length) {
            const [accountNo, bindRequestNo, cardApplyId] = dataArr;
            this.boundOrderDataForNS = { accountNo, bindRequestNo, cardApplyId };
            // eslint-disable-next-line no-console
            console.log('BindCardNativeData:', dataArr);

            nativeLocalRemoveData({ key: NS_QUICK_BOUND_ORDER_DATA });
            this.queryQuickBindFinalResult();
          }
        } catch (err) {
          ubtSendError(`Quick Bind Get Data Error: ${(err as Error)?.message}`);
        }
      }
    }, false);
    this.clearBackOriginCardBindLister = clearBackLister;
  }

  private beforeDestroy() {
    this.clearAllToast();
    /** 删除可能未使用&清理的绑卡数据 */
    nativeLocalRemoveData({ key: NS_QUICK_BOUND_ORDER_DATA });
    /** 清除回退监听事件 */
    if (typeof this.clearBackOriginCardBindLister === 'function') {
      this.clearBackOriginCardBindLister();
    }
  }

  private showLoadingResult() {
    Toast.loading({
      message: '绑卡结果查询中...',
      duration: 0,
      forbidClick: true,
    });
  }

  /**
   * 清除所有Toast信息
   */
  private clearAllToast() {
    Toast.clear();
  }

  /**
   * 查询一键绑卡的结果
   */
  private async queryQuickBindResult() {
    try {
      const { accountNo, bindRequestNo, cardApplyId } = this.boundOrderData;
      const { result, bankCardId } = await cardQuickBindQuery({
        bankCardNo: accountNo,
        thirdCardApplyNo: bindRequestNo,
        cardApplyId,
      });

      if (result === BindCardResultEnum.SUCCESS) {
        Toast('绑卡成功');
        /** 保存绑卡成功记录，规避history.back的场景重复触发 */
        session.set(BIND_CARD_SUCCESS_SIGN, bindRequestNo);
        this.$ubtModuleExposeNew('finance_cardBind_quickBinding_success');
        this.$emit('successQuickBindCard', {
          bankId: bankCardId,
          bankNo: accountNo,
          /** 新开Webview方式，则取选中的银行信息：bankName、bankIcon同步到父组件 */
          bankName: this.selectedBankData?.bankName,
          bankLogo: this.selectedBankData?.bankLogo,
        }, this.isOriginCardBindPageWebview);
      } else if (result === BindCardResultEnum.PROCESS) {
        this.showLoadingResult();
        return Promise.resolve(false);
      } else if (result === BindCardResultEnum.FAILURE) {
        Toast('绑卡失败，可尝试输卡号添加');
        this.$emit('failureQuickBindCard', {
          bankId: bankCardId,
          bankNo: accountNo,
        });
      }
    } catch (err) {
      Toast((err as Error).message);
      return Promise.resolve(false);
    }
    return Promise.resolve(true);
  }

  /**
   * 关闭同意协议对话框
   */
  private closeAgreeProtocolDialog() {
    this.agreeProtocolDialogVisible = false;
    this.selectedBankData = null;
  }

  /**
   * 提交绑卡订单
   */
  private async submitBindCardOrder() {
    if (!this.selectedBankData) {
      this.agreeProtocolDialogVisible = false;
      return;
    }
    this.$ubtPageButtonNew('finance_cardBind_quickBinding_confirm_agrees', {
      bankCode: this.selectedBankData.bankCode,
    });

    const { jumpUrl } = await cardQuickBindApply({
      thirdBankCode: this.selectedBankData.thirdBankCode || '',
      bindCardFlowType: this.bindCardScene,
      commodityId: this.productId,
      capitalCode: this.capitalCode,
      callBackUrl: this.quickBindCardBackLink,
    });

    if (!jumpUrl) {
      ubtSendError('QuickBindApply result: jumpUrl is null ');
      return;
    }

    this.$emit('beforeLeaveBindCardPage', {
      ...this.selectedBankData,
    });

    if (this.isUseNewWebviewToJumpCommunity) {
      this.isOriginCardBindPageWebview = true;
      openThirdPage(jumpUrl);
    } else {
      session.set(BIND_CARD_BACK_LINK, this.quickBindCardBackLink);
      /** mock通联测试链路，勿删 */
      // window.location.href = `https://coreopsis.lmh.com/index.html?jumpUrl=${window.encodeURIComponent(window.location.href)}`;
      window.location.href = jumpUrl;
    }

    this.agreeProtocolDialogVisible = false;
  }

  /**
   * 查看绑卡待签署的协议
   * @param bankData 银行数据
   */
  private viewAgreeProtocols(bankData: IBankData) {
    this.selectedBankData = bankData;
    this.agreeProtocolDialogVisible = true;

    this.$emit('startQuickBindCard', bankData);
    this.$ubtPageButtonNew('finance_cardBind_quickBinding_select_bank', {
      bankCode: bankData.bankCode,
    });
  }

  private render() {
    if (this.componentStatus === QuickBindCardStatusEnum.LOADING) {
      return <div class={style.cardSkeletonBox}></div>;
    }

    if (this.cardListData && this.cardListData.length) {
      return (
        <div class={style.quickBindCard}>
          <p class={style.title}>{this.title}</p>
          <div class={style.cardList}>
          {this.cardListData.map((item) => (
            <BankCard
              key={item.bankCode}
              bankData={item}
              onViewAgreeProtocols={() => this.viewAgreeProtocols(item)}
            />
          ))}
          </div>
          <AgreeProtocolDialog
            visible={this.agreeProtocolDialogVisible}
            bankData={this.selectedBankData}
            capitalCode={this.capitalCode}
            productId={this.productId}
            onCancel={() => this.closeAgreeProtocolDialog()}
            onConfirm={() => this.submitBindCardOrder()}
          />
        </div>
      );
    }

    return null;
  }
}
